import "./Agents.css";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Selector } from "../../components/common/Selector";
import { fetchAgents, fetchAgentMetrics, Agent, AgentMetrics } from "../../api/agents";
import { useGlobal } from "../../context/GlobalContext";
import { AgentExpanded } from "../../components/agents/AgentExpanded";
import { AgentsCollapsed } from "../../components/agents/AgentsCollapsed";

export function Agents() {
    const { selectedProjectKey, userKey, isLoading: contextLoading } = useGlobal();
    const [searchParams] = useSearchParams();
    const agentName = searchParams.get('name');
    const [selectedTime, setSelectedTime] = useState('24h');
    const [agents, setAgents] = useState<Agent[]>([]);
    const [metrics, setMetrics] = useState<Map<string, AgentMetrics>>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadAgents = async () => {
            if (!selectedProjectKey || !userKey || contextLoading) return;

            setLoading(true);
            setError('');

            try {
                const [agentsData, metricsData] = await Promise.all([
                    fetchAgents(userKey, selectedProjectKey),
                    fetchAgentMetrics(userKey, selectedProjectKey, selectedTime)
                ]);
                
                setAgents(agentsData);
                
                const metricsMap = new Map<string, AgentMetrics>();
                metricsData.forEach(metric => {
                    metricsMap.set(metric.agentName, metric);
                });
                setMetrics(metricsMap);
            } catch (err) {
                setError('Failed to load agents');
            } finally {
                setLoading(false);
            }
        };
        loadAgents();
    }, [selectedProjectKey, userKey, contextLoading, selectedTime]);

    const timeOptions = [
        { key: '1h', label: '1 hour' },
        { key: '8h', label: '8 hours' },
        { key: '24h', label: '24 hours' },
        { key: '7d', label: '7 days' },
        { key: '15d', label: '15 days' },
        { key: '30d', label: '30 days' }
    ];

    const selectedAgent = agentName ? agents.find(a => a.agentName === agentName) : null;

    return (
        <div className="agents-page">
            {selectedAgent ? (
                <AgentExpanded agent={selectedAgent} />
            ) : (
                <>
                    <div className="agents-header">
                        <h1 className="agents-title">Agents</h1>
                        <div className="time-selector">
                            <Selector
                                options={timeOptions}
                                selectedKey={selectedTime}
                                onSelect={setSelectedTime}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="agents-loading">Loading agents...</div>
                    ) : error ? (
                        <div className="agents-error">{error}</div>
                    ) : agents.length === 0 ? (
                        <div className="agents-empty">No agents found</div>
                    ) : (
                        <AgentsCollapsed agents={agents} metrics={metrics} />
                    )}
                </>
            )}
        </div>
    );
}
